"use client";
import { useTranslations, useMessages } from "next-intl";

/**
 * Drop-in replacement for Refine's useTranslate() in App Router client components.
 * Uses next-intl under the hood. Supports the same call signatures:
 *   const t = useAppTranslate();
 *   t("order.title")  // nested key lookup
 *   t("key", "fallback")  // with fallback
 *   t("key", { count: 5 })  // with options/interpolation
 *   t("key", { count: 5 }, "fallback")  // with options and fallback
 *   t("key", { fieldName: "value" })  // with string interpolation variables
 *
 * Also compatible with i18next TFunction signature for yup schemas.
 *
 * Note: Handles conversion from i18next {{var}} syntax to manual string interpolation.
 */
type AppTranslate = (
  key: string,
  fallbackOrOptions?: string | Record<string, unknown>,
  defaultMessageOrOptions?: string | Record<string, unknown>
) => string;

export function useAppTranslate(): AppTranslate {
  const tIntl = useTranslations();
  const messages = useMessages();

  // Helper to safely get a nested value from messages
  const getMessageSafely = (obj: unknown, path: string): unknown => {
    try {
      return path
        .split(".")
        .reduce<unknown>(
          (current, part) => (current as Record<string, unknown>)?.[part],
          obj
        );
    } catch {
      return undefined;
    }
  };

  // Helper to do {{var}} (i18next legacy) AND {var} (ICU / next-intl native) interpolation
  const interpolate = (
    str: string,
    opts?: Record<string, unknown>
  ): string => {
    if (!opts) return str;
    return str
      .replace(/\{\{(\w+)\}\}/g, (match, varName) =>
        String(opts[varName] ?? match)
      )
      .replace(/\{(\w+)\}/g, (match, varName) =>
        varName in opts ? String(opts[varName]) : match
      );
  };

  // Supports both Refine-style t(key, fallback) and i18next TFunction signatures
  return (
    key: string,
    fallbackOrOptions?: string | Record<string, unknown>,
    defaultMessageOrOptions?: string | Record<string, unknown>
  ): string => {
    // Determine fallback and options
    let fallback: string;
    let options: Record<string, unknown> | undefined;

    if (typeof fallbackOrOptions === "string") {
      // Called as t(key, fallback) or t(key, defaultValue, options) i18next style
      if (typeof defaultMessageOrOptions === "object" && defaultMessageOrOptions !== null) {
        // i18next pattern: t(key, "default {{var}}", { var: value })
        fallback = fallbackOrOptions;
        options = defaultMessageOrOptions;
      } else {
        // Refine pattern: t(key, fallback) or t(key, fallback, defaultMessage)
        fallback = fallbackOrOptions;
        options = undefined;
      }
    } else if (typeof fallbackOrOptions === "object" && fallbackOrOptions !== null) {
      // Called as t(key, options) or t(key, options, fallback)
      options = fallbackOrOptions;
      fallback = (typeof defaultMessageOrOptions === "string" ? defaultMessageOrOptions : null) ?? key;
    } else {
      fallback = (typeof defaultMessageOrOptions === "string" ? defaultMessageOrOptions : null) ?? key;
      options = undefined;
    }

    try {
      // Try to get the translation using next-intl
      let result: string;

      // next-intl reports MISSING_MESSAGE (key absent) and INVALID_MESSAGE
      // (legacy {{var}} syntax — not valid ICU) through its onError handler
      // BEFORE control reaches the catch below, spamming the console even though
      // we recover. Resolve the raw message up front and bypass tIntl for those
      // two cases: the rendered text is identical, just without the noise.
      const preResolved = getMessageSafely(messages, key);
      if (typeof preResolved !== "string") {
        // Missing key → use the caller's fallback (or the key) directly.
        return options ? interpolate(fallback, options) : fallback;
      }
      if (preResolved.includes("{{")) {
        // Legacy i18next {{var}} interpolation — invalid ICU, do it manually.
        return interpolate(preResolved, options);
      }

      // Pass options to next-intl for native {var} (ICU) handling.
      // For legacy {{var}} messages, next-intl throws INVALID_MESSAGE / FORMATTING_ERROR;
      // we catch and fall back to manual interpolation on the raw message.
      try {
        result = tIntl(
          key as Parameters<typeof tIntl>[0],
          (options || {}) as Parameters<typeof tIntl>[1]
        );
      } catch (nextIntlError: unknown) {
        const rawMessage = getMessageSafely(messages, key);
        if (typeof rawMessage === "string") {
          return interpolate(rawMessage, options);
        }
        if (options && fallback !== key) {
          return interpolate(fallback, options);
        }
        if (process.env.NODE_ENV === "development") {
          console.warn(`[useAppTranslate] next-intl error for key "${key}":`, (nextIntlError as { message?: string })?.message);
        }
        return fallback;
      }

      // next-intl may also "silently" return a fallback string via getMessageFallback/onError
      // (without throwing). If the returned string still contains an unresolved {var}/{{var}}
      // placeholder and we have options, manually interpolate it so the user never sees raw templates.
      if (typeof result === "string" && options && /\{\{?\w+\}?\}/.test(result)) {
        const interpolated = interpolate(result, options);
        if (interpolated !== result) return interpolated;
      }

      // If next-intl returned something non-string (unresolved key → object) or the key itself,
      // try the raw message from the bundle directly.
      if (typeof result !== "string" || result === key) {
        const rawMessage = getMessageSafely(messages, key);
        if (typeof rawMessage === "string") {
          return interpolate(rawMessage, options);
        }
      }

      // If result is an object, it means it couldn't resolve the key
      if (typeof result === "object" && result !== null) {
        return fallback;
      }

      // If result is the key itself (untranslated), use fallback
      if (result === key && fallback !== key) {
        return fallback;
      }

      // Handle i18next {{var}} syntax by doing manual interpolation
      if (typeof result === "string" && options) {
        return interpolate(result, options);
      }

      return result ?? fallback;
    } catch (error) {
      // If any error occurs, return fallback to keep the app from crashing
      if (process.env.NODE_ENV === "development") {
        console.warn(`[useAppTranslate] Error translating key "${key}":`, error);
      }
      return fallback;
    }
  };
}
